<template>
  <main class="detail-layout-page">
    <section class="section">
      <div class="container">
        <DetailLayoutTitle :title :lead :rating :is-sustainable />

        <div class="detail-layout-page-grid">
          <div class="detail-layout-page-media">
            <DetailLayoutSlider :images="sliderImages" />
          </div>
          <div v-if="$slots.cta" ref="cta" class="sidebar-block is-cta">
            <slot name="cta" />
          </div>

          <div
            v-if="hasTabNavigation"
            class="sidebar-block is-tabnav tablet:hidden"
            :style="tabNavStyle"
          >
            <ScrollSpy v-if="hasTabNavigation" :tabs="tabs" />
          </div>

          <DetailLayoutSpecialOffers
            :special-offers
            :class="{ 'has-margin-top': $slots.cta }"
          />

          <div v-if="features.length || $slots.features" class="sidebar-block">
            <slot name="features">
              <Features :features="features" :is-small="!largeFeatures" />
            </slot>
          </div>
          <div v-if="reviewUrl" class="sidebar-block">
            <DetailLayoutReview
              v-bind="reviewUrl"
              :item-reviewed-name="title"
            />
          </div>

          <div v-if="zurichCardBenefit" class="sidebar-block">
            <DetailLayoutZurichCard :benefit="zurichCardBenefit" />
          </div>

          <div v-if="$slots.links" class="sidebar-block">
            <slot name="links" />
          </div>
          <div v-if="$slots.aside" class="sidebar-block">
            <slot name="aside" />
          </div>

          <slot name="events" />

          <div class="detail-layout-page-main">
            <div class="detail-layout-page-content">
              <DetailLayoutSection anchor="content">
                <div class="content" v-html="body" />
                <slot name="content" />
              </DetailLayoutSection>

              <DetailLayoutSection
                v-for="v in slots"
                :key="v.key"
                :title="v.title"
                :anchor="v.key"
              >
                <slot :name="v.key" />
              </DetailLayoutSection>
            </div>
          </div>
        </div>
      </div>
    </section>

    <slot name="below" />

    <SharingButtons />
  </main>
</template>

<script lang="ts" setup>
import type {
  AccomodationRatingFragment,
  AddressCardFragment,
  AddressOpeningHoursFragment,
  MediaImageFragment,
  MediaCantoFragment,
  StorageStaticUrlFragment,
  VenueSpecialOfferFragment,
} from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import type { FeatureItem } from '~/components/Features/index.vue'

const props = withDefaults(
  defineProps<{
    nid?: string
    title?: string
    lead?: string
    subtitle?: string
    body?: string
    zurichCardBenefit?: string
    isSustainable?: boolean
    rating?: AccomodationRatingFragment
    features?: FeatureItem[]
    largeFeatures?: boolean
    keyVisual?: MediaImageFragment | MediaCantoFragment | object | null
    images?: Array<MediaImageFragment | MediaCantoFragment | object | null>
    address?: AddressCardFragment
    openingHours?: AddressOpeningHoursFragment
    reviewUrl?: StorageStaticUrlFragment
    specialOffers?: VenueSpecialOfferFragment[]
  }>(),
  {
    nid: '',
    title: '',
    lead: '',
    subtitle: '',
    body: '',
    zurichCardBenefit: '',
    isSustainable: false,
    rating: undefined,
    keyVisual: undefined,
    address: undefined,
    openingHours: undefined,
    features: () => [],
    largeFeatures: false,
    images: () => [],
    reviewUrl: undefined,
    specialOffers: () => [],
  },
)

const sliderImages = computed(
  () =>
    [props.keyVisual, ...props.images].filter(notNullish) as
      MediaImageFragment[] | MediaCantoFragment[],
)

const { $texts } = useEasyTexts()
const { width } = useViewport()

const ctaHeight = ref(0)

const tabNavStyle = computed(() => {
  return {
    top: `${ctaHeight.value}px`,
  }
})

const dynamicSlots = computed(() => {
  return {
    languages: $texts('languages'),
    price: $texts('price'),
    timeAndDuration: $texts('timeAndDuration'),
    openingHours: $texts('openingHours'),
    additionalInformation: $texts('additionalInformation'),
    panorama: $texts('panorama'),
    virtualTour: $texts('virtualTour'),
    video: $texts('video'),
    address: $texts('address'),
    startingPoint: $texts('startingPoint'),
  }
})

const renderedSlots = useSlots()

const slots = computed(() => {
  return Object.entries(dynamicSlots.value)
    .map(([key, title]) => {
      return {
        key,
        title,
      }
    })
    .filter((v) => !!renderedSlots[v.key])
})

const tabs = computed(() => {
  return [
    {
      id: 'content',
      label: $texts('description'),
    },
    ...slots.value.map((v) => {
      return {
        id: v.key,
        label: v.title,
      }
    }),
  ]
})

const hasTabNavigation = computed(() => tabs.value.length > 1)

function setCtaHeight() {
  const refCta = document.querySelector('.is-cta') as HTMLElement
  ctaHeight.value = (refCta?.clientHeight || 0) - 2
}

watch(width, () => {
  setCtaHeight()
})

onMounted(() => {
  setCtaHeight()
})
</script>

<style>
@reference '~/assets/css/tailwind.css';
.detail-layout-page-title {
  @apply mb-size-4 widescreen:mb-size-1;
  .no-wrap {
    @apply tablet:whitespace-nowrap;
  }
  @variant widescreen {
    grid-column: 1 / 3;
    grid-row: 1;
  }

  .rating {
    @apply inline-block text-[0.8em] select-none;
    vertical-align: 0.1em;
  }
}

.detail-layout-page-features {
  @apply mt-size-3;
}

.detail-layout-page-grid {
  @apply tablet:grid;
  @variant tablet {
    grid-template-columns: 1fr 17rem;
  }
  @variant desktop {
    grid-template-columns: 1fr 18rem;
  }
  @variant widescreen {
    grid-template-columns: 1fr 22rem;
  }
}

.detail-layout-page-header {
  .section {
    @apply pb-0;
  }
}

.sidebar-block {
  @apply border-t border-b border-solid border-border p-[1rem];
  grid-column: 2;
  margin-top: -1px;
  margin-left: -1rem;
  margin-right: -1rem;
  @apply tablet:mx-0 tablet:ml-0 tablet:py-[1rem] tablet:pr-0 tablet:pl-[1rem];
  @apply widescreen:ml-size-5 widescreen:px-0 widescreen:py-size-5;

  .button {
    @apply belowDesktop:text-size-7;
  }

  &.is-cta {
    @apply sticky top-0 z-50 mt-0 border-t-0 bg-background;
    @apply tablet:py-[0.75rem] tablet:pr-0 tablet:pl-[1rem];
    @apply desktop:py-[1rem] desktop:pr-0 desktop:pl-[1rem];
    @apply widescreen:p-size-5 widescreen:pr-0;
    top: var(--sticky-top);
    @variant desktop {
      margin-top: -1rem;
    }
    @variant widescreen {
      padding-left: 0 !important;
    }
  }
  &.is-tabnav {
    @apply sticky z-[49] overflow-auto bg-scheme-main;
    @apply tablet:pt-[1rem];
    @apply belowTablet:border-0 belowTablet:py-0;
    @variant belowTablet {
      margin: -1px -1rem 0;
    }
    @variant tablet {
      grid-row: span 17;
      top: inherit;
    }
  }
}

.detail-layout-page-media {
  margin: 0 -1rem;
  @variant tablet {
    margin: 0;
    grid-column: 1 / 3;
    grid-row: 1 / 2;
    min-width: 0;
  }
  @variant desktop {
    grid-column: 1;
    grid-row: 1 / 60;
  }
}

.detail-layout-page-main {
  @apply min-w-0;
  @apply tablet:border-r tablet:border-solid tablet:border-border;
  @variant tablet {
    grid-row: 2 / 10;
    grid-column: 1;
  }
  @variant desktop {
    grid-row: 1 / 60;
    padding-top: 56.25%;
  }
}
.detail-layout-page-content {
  @apply min-w-0 pt-size-4;
  @apply tablet:pt-size-4 tablet:pr-size-4;
  @apply desktop:pt-size-3 desktop:pr-size-3;
  @apply widescreen:pt-size-2;
  @variant tablet {
    padding-right: 1.8125rem;
  }
}
</style>
